// ABOUTME: React Query hook for searching scopes via the TUC Configs scope navigator search API.
// ABOUTME: Calls GET /tuc-configs/{entityType}/scopes/search and maps the response to ScopeResultItem[].

import { useCallback } from 'react'

import { api } from '../../../../App.store'
import { ScopeLevel, ScopeResultItem, VerifyScopeResult, EntityType, getEntityApiPath, LEVEL_TO_API, apiToLevel } from '../types'

interface ApiScopeSearchResult {
  id?: string
  level: string
  parentId?: string
  countryId: string
  businessLine: string
  displayName: string
  displayNumber?: string
  scopeData?: Record<string, string>
  isOverridden: boolean
}

const searchScopesApi = async (
  entityType: EntityType,
  countryId: string,
  businessLine: string,
  level: ScopeLevel,
  searchTerm: string
): Promise<VerifyScopeResult> => {
  const res = await api.get<ApiScopeSearchResult[]>(
    `/tuc-configs/${getEntityApiPath(entityType)}/scopes/search`,
    {
      params: {
        countryId,
        businessLine: businessLine.toUpperCase(),
        level: LEVEL_TO_API[level],
        searchTerm: searchTerm || undefined
      }
    }
  )

  const results: ScopeResultItem[] = (res.data ?? []).map((item) => ({
    id: item.id ?? null,
    parentId: item.parentId,
    displayName: item.displayName,
    displayNumber: item.displayNumber,
    level: apiToLevel(item.level) ?? level,
    scopeData: item.scopeData,
    isOverridden: item.isOverridden
  }))

  return {
    found: results.length > 0,
    results
  }
}

/**
 * Hook that returns a memoized search function for scope search.
 * The search function can be passed as a callback to child components.
 */
export const useSearchScopes = (
  entityType: EntityType | undefined,
  countryId: string | undefined,
  businessLine: string | undefined
) => {
  const search = useCallback(async (level: ScopeLevel, searchTerm: string): Promise<VerifyScopeResult> => {
    if (!entityType || !countryId || !businessLine) return { found: false }
    return searchScopesApi(entityType, countryId, businessLine, level, searchTerm)
  }, [entityType, countryId, businessLine])

  return { search }
}
